import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  ArrowLeft, Brain, Plus, Pencil, Trash2, Copy, ChevronDown, ChevronUp,
  Shield, Sparkles, Factory, Heart, Pill, ShoppingBag, Store, Code,
  LineChart, GraduationCap, Utensils, AlertTriangle, Cog, Save, X
} from 'lucide-react';

const ICON_OPTIONS = [
  { value: 'Brain', label: 'Brain' },
  { value: 'Heart', label: 'Heart' },
  { value: 'Shield', label: 'Shield' },
  { value: 'Users', label: 'Users' },
  { value: 'MessageCircle', label: 'Message' },
  { value: 'Scale', label: 'Scale' },
  { value: 'Code', label: 'Code' },
  { value: 'LineChart', label: 'Chart' },
  { value: 'GraduationCap', label: 'Education' },
  { value: 'ShoppingBag', label: 'Shopping' },
  { value: 'AlertTriangle', label: 'Alert' },
  { value: 'Cog', label: 'Settings' },
  { value: 'Pill', label: 'Pharma' },
  { value: 'Store', label: 'Store' },
  { value: 'Factory', label: 'Factory' },
];

const COLOR_OPTIONS = [
  '#1A2C3A', '#2C4C3B', '#4A6B8C', '#C16E5A', '#D4AF37',
  '#DC2626', '#7C3AED', '#0891B2', '#EA580C', '#059669',
  '#1D4ED8', '#6366F1', '#E11D48', '#0D9488', '#B45309',
  '#9333EA', '#475569', '#16A34A', '#F59E0B', '#0284C7',
];

const CATEGORY_OPTIONS = ['Communication', 'Leadership', 'Strategy', 'Negotiation', 'Crisis Management', 'Customer Relations', 'Custom'];

const EMPTY_SCENARIO = {
  title: '', description: '', category: 'Communication', industry: '', icon: 'Brain',
  color: '#4A6B8C', estimated_time: '10-15 min', skills: [], avatar_name: 'AI Partner',
  difficulties: [
    { level: 'beginner', system_prompt: '' },
    { level: 'intermediate', system_prompt: '' },
    { level: 'advanced', system_prompt: '' },
  ],
  variations: [{ id: 'default', title: 'Default', description: 'Standard scenario' }],
  report_instructions: '',
  report_frameworks: [],
};

export default function ScenarioBuilder() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, object = editing
  const [showTemplates, setShowTemplates] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [frameworkInput, setFrameworkInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchData(); }, [axiosAuth]);

  const fetchData = async () => {
    try {
      const api = axiosAuth();
      const [scenRes, tmplRes] = await Promise.all([
        api.get('/admin/scenarios'),
        api.get('/admin/scenarios/templates'),
      ]);
      setScenarios(scenRes.data.scenarios);
      setTemplates(tmplRes.data.templates);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const startNew = () => {
    setEditing({ ...JSON.parse(JSON.stringify(EMPTY_SCENARIO)), _isNew: true });
  };

  const startFromTemplate = (tmpl) => {
    setEditing({
      ...JSON.parse(JSON.stringify(tmpl)),
      _isNew: true,
    });
    setShowTemplates(false);
  };

  const startEdit = (scenario) => {
    setEditing({ ...JSON.parse(JSON.stringify(scenario)), _isNew: false });
  };

  const saveScenario = async () => {
    if (!editing.title.trim()) { toast.error('Title is required'); return; }
    if (!editing.description.trim()) { toast.error('Description is required'); return; }
    const hasPrompt = editing.difficulties.some(d => d.system_prompt.trim());
    if (!hasPrompt) { toast.error('At least one difficulty level needs a system prompt'); return; }

    setSaving(true);
    try {
      const api = axiosAuth();
      const payload = {
        title: editing.title,
        description: editing.description,
        category: editing.category,
        industry: editing.industry || '',
        icon: editing.icon,
        color: editing.color,
        estimated_time: editing.estimated_time,
        skills: editing.skills,
        avatar_name: editing.avatar_name,
        difficulties: editing.difficulties.filter(d => d.system_prompt.trim()),
        variations: editing.variations.length > 0 ? editing.variations : [{ id: 'default', title: 'Default', description: 'Standard scenario' }],
        report_instructions: editing.report_instructions || '',
        report_frameworks: editing.report_frameworks || [],
      };

      if (editing._isNew) {
        await api.post('/admin/scenarios', payload);
        toast.success('Scenario created');
      } else {
        await api.put(`/admin/scenarios/${editing.id}`, payload);
        toast.success('Scenario updated');
      }
      setEditing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const deleteScenario = async (id) => {
    try {
      const api = axiosAuth();
      await api.delete(`/admin/scenarios/${id}`);
      toast.success('Scenario deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const updateField = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
  };

  const updateDifficulty = (level, prompt) => {
    setEditing(prev => ({
      ...prev,
      difficulties: prev.difficulties.map(d => d.level === level ? { ...d, system_prompt: prompt } : d),
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !editing.skills.includes(skillInput.trim())) {
      updateField('skills', [...editing.skills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    updateField('skills', editing.skills.filter(s => s !== skill));
  };

  const addVariation = () => {
    const id = `var-${Date.now()}`;
    updateField('variations', [...editing.variations, { id, title: '', description: '' }]);
  };

  const updateVariation = (idx, field, value) => {
    const updated = [...editing.variations];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditing(prev => ({ ...prev, variations: updated }));
  };

  const removeVariation = (idx) => {
    if (editing.variations.length <= 1) return;
    updateField('variations', editing.variations.filter((_, i) => i !== idx));
  };

  const addFramework = () => {
    if (frameworkInput.trim() && !editing.report_frameworks.includes(frameworkInput.trim())) {
      setEditing(prev => ({ ...prev, report_frameworks: [...(prev.report_frameworks || []), frameworkInput.trim()] }));
      setFrameworkInput('');
    }
  };

  const removeFramework = (fw) => {
    setEditing(prev => ({ ...prev, report_frameworks: (prev.report_frameworks || []).filter(f => f !== fw) }));
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="heritage-card p-8 text-center">
          <div className="w-8 h-8 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body">Loading scenario builder...</p>
        </div>
      </div>
    );
  }

  // ── Editor View ──
  if (editing) {
    return (
      <div className="min-h-screen bg-background" data-testid="scenario-editor">
        <nav className="border-b border-border bg-surface sticky top-0 z-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button data-testid="editor-back-btn" onClick={() => setEditing(null)}
                className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-muted transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <span className="font-heading text-lg font-bold">
                {editing._isNew ? 'Create Scenario' : 'Edit Scenario'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="editor-cancel-btn" onClick={() => setEditing(null)}
                className="h-8 px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-sm font-medium rounded-lg shadow-none">
                <X className="w-3.5 h-3.5" /> Cancel
              </Button>
              <Button data-testid="editor-save-btn" onClick={saveScenario} disabled={saving}
                className="h-8 px-3 bg-brand text-[#333333] gap-1.5 text-sm font-medium rounded-lg hover:opacity-90">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving...' : 'Save Scenario'}
              </Button>
            </div>
          </div>
        </nav>

        <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
          {/* Basic Info */}
          <section className="heritage-card p-6 space-y-4">
            <h2 className="font-heading text-base font-bold">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="font-body text-sm font-medium mb-1 block">Title *</label>
                <input data-testid="scenario-title-input" type="text" value={editing.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className="heritage-input w-full text-sm" placeholder="e.g., Breaking Bad News to a Patient" />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-sm font-medium mb-1 block">Description *</label>
                <textarea data-testid="scenario-desc-input" value={editing.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="heritage-input w-full text-sm min-h-[80px] resize-y"
                  placeholder="Describe what this scenario practices..." />
              </div>
              <div>
                <label className="font-body text-sm font-medium mb-1 block">Category</label>
                <Select value={editing.category} onValueChange={(v) => updateField('category', v)}>
                  <SelectTrigger data-testid="scenario-category-select" className="w-full border border-border h-9 font-body text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border border-border shadow-elevated bg-surface">
                    {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c} className="font-body text-sm">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-body text-sm font-medium mb-1 block">Industry / Domain</label>
                <input data-testid="scenario-industry-input" type="text" value={editing.industry || ''}
                  onChange={(e) => updateField('industry', e.target.value)}
                  className="heritage-input w-full text-sm" placeholder="e.g., Healthcare, Retail, Pharma" />
              </div>
              <div>
                <label className="font-body text-sm font-medium mb-1 block">AI Partner Name</label>
                <input data-testid="scenario-avatar-input" type="text" value={editing.avatar_name}
                  onChange={(e) => updateField('avatar_name', e.target.value)}
                  className="heritage-input w-full text-sm" placeholder="e.g., Dr. Chen, Customer Kim" />
              </div>
              <div>
                <label className="font-body text-sm font-medium mb-1 block">Estimated Time</label>
                <input type="text" value={editing.estimated_time}
                  onChange={(e) => updateField('estimated_time', e.target.value)}
                  className="heritage-input w-full text-sm" placeholder="10-15 min" />
              </div>
            </div>

            {/* Icon & Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-sm font-medium mb-1 block">Icon</label>
                <Select value={editing.icon} onValueChange={(v) => updateField('icon', v)}>
                  <SelectTrigger className="w-full border border-border h-9 font-body text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border border-border shadow-elevated bg-surface">
                    {ICON_OPTIONS.map(i => <SelectItem key={i.value} value={i.value} className="font-body text-sm">{i.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-body text-sm font-medium mb-1 block">Color</label>
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_OPTIONS.map(c => (
                    <button key={c} onClick={() => updateField('color', c)}
                      className={`w-7 h-7 rounded-md border-2 transition-all ${editing.color === c ? 'border-navy scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Skills */}
            <div>
              <label className="font-body text-sm font-medium mb-1 block">Skills Practiced</label>
              <div className="flex gap-2 mb-2">
                <input data-testid="skill-input" type="text" value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="heritage-input flex-1 text-sm" placeholder="Add a skill and press Enter" />
                <Button onClick={addSkill} className="heritage-btn bg-surface border border-border text-foreground hover:bg-muted h-9 px-3 text-sm">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {editing.skills.map((s) => (
                  <Badge key={`skill-${s}`} className="bg-muted text-foreground border border-border font-body text-xs px-2 py-0.5 gap-1 cursor-pointer hover:bg-stone/50"
                    onClick={() => removeSkill(s)}>
                    {s} <X className="w-3 h-3" />
                  </Badge>
                ))}
              </div>
            </div>
          </section>

          {/* Difficulty Prompts */}
          <section className="heritage-card p-6 space-y-4">
            <h2 className="font-heading text-base font-bold">AI Behavior Prompts *</h2>
            <p className="font-body text-xs text-slate">Define how the AI partner should behave at each difficulty level. At least one is required.</p>
            {['beginner', 'intermediate', 'advanced'].map(level => {
              const diff = editing.difficulties.find(d => d.level === level) || { level, system_prompt: '' };
              return (
                <div key={level}>
                  <label className="font-body text-sm font-medium mb-1 flex items-center gap-2">
                    <Badge className={`border-none font-body text-[10px] px-1.5 py-0 ${level === 'beginner' ? 'bg-success/10 text-success' : level === 'intermediate' ? 'bg-warning/10 text-warning' : 'bg-accent/10 text-accent'}`}>
                      {level}
                    </Badge>
                    System Prompt
                  </label>
                  <textarea data-testid={`prompt-${level}`} value={diff.system_prompt}
                    onChange={(e) => updateDifficulty(level, e.target.value)}
                    className="heritage-input w-full text-sm min-h-[120px] resize-y font-mono text-xs leading-relaxed"
                    placeholder={`Describe the AI's personality, behavior, and scenario context for ${level} difficulty...`} />
                </div>
              );
            })}
          </section>


          {/* Report Customization */}
          <section className="heritage-card p-6 space-y-4" data-testid="report-customization-section">
            <h2 className="font-heading text-base font-bold">Report Customization</h2>
            <p className="font-body text-xs text-muted-foreground">Configure how performance reports are generated for this scenario. These instructions will be used by the AI scoring engine.</p>

            <div>
              <label className="font-body text-sm font-medium mb-1.5 block">Report Instructions</label>
              <textarea data-testid="report-instructions-input"
                value={editing.report_instructions || ''}
                onChange={(e) => setEditing(prev => ({ ...prev, report_instructions: e.target.value }))}
                className="heritage-input w-full text-sm min-h-[100px]" rows={4}
                placeholder={"E.g., Focus the report on the participant's ability to demonstrate active listening and empathy. Evaluate whether they used open-ended questions. Score harshly on interrupting behavior. The report should highlight specific moments where the participant could have shown more patience. Include a section on body language cues (tone of voice inferred from text)."} />
              <p className="font-body text-[10px] text-muted-foreground mt-1">These instructions customize the AI scoring engine for this specific scenario. Be specific about what to evaluate, how strictly to score, and what sections to emphasize.</p>
            </div>

            <div>
              <label className="font-body text-sm font-medium mb-1.5 block">Mandatory Frameworks</label>
              <p className="font-body text-[10px] text-muted-foreground mb-2">Add specific frameworks/models the report MUST include (e.g., PEARL, SBI, 5 Whys, Listening Loop, GROW Model)</p>
              <div className="flex gap-2 mb-2">
                <input data-testid="framework-input" type="text" value={frameworkInput}
                  onChange={(e) => setFrameworkInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFramework(); } }}
                  className="heritage-input flex-1 text-sm" placeholder="e.g., PEARL Framework" />
                <Button data-testid="add-framework-btn" onClick={addFramework}
                  className="heritage-btn bg-surface border border-border text-foreground hover:bg-muted gap-1.5 h-9 px-3 text-xs">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {(editing.report_frameworks || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="framework-tags">
                  {editing.report_frameworks.map((fw) => (
                    <span key={`fw-${fw}`} className="inline-flex items-center gap-1 px-2 py-1 bg-brand/10 text-brand rounded-md text-xs font-medium">
                      {fw}
                      <button onClick={() => removeFramework(fw)} className="hover:text-accent">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>


          {/* Variations */}
          <section className="heritage-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-bold">Scenario Variations</h2>
              <Button data-testid="add-variation-btn" onClick={addVariation}
                className="heritage-btn bg-surface border border-border text-foreground hover:bg-muted gap-1.5 h-8 px-3 text-xs">
                <Plus className="w-3.5 h-3.5" /> Add Variation
              </Button>
            </div>
            {editing.variations.map((v, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-muted/50 rounded-lg p-3">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <input type="text" value={v.title} onChange={(e) => updateVariation(idx, 'title', e.target.value)}
                    className="heritage-input text-sm" placeholder="Variation title" data-testid={`variation-title-${idx}`} />
                  <input type="text" value={v.description} onChange={(e) => updateVariation(idx, 'description', e.target.value)}
                    className="heritage-input text-sm" placeholder="Brief description" data-testid={`variation-desc-${idx}`} />
                </div>
                {editing.variations.length > 1 && (
                  <button onClick={() => removeVariation(idx)}
                    className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-destructive/10 transition-colors flex-shrink-0 mt-0.5">
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                )}
              </div>
            ))}
          </section>
        </main>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="min-h-screen bg-background" data-testid="scenario-builder-page">
      <Navbar showBack backTo="/admin" backLabel="Scenario Builder" />

      <main className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="overline mb-2">Admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Custom Scenarios</h1>
            <p className="font-body text-base text-slate mt-2">Create industry-specific scenarios or start from templates</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="from-template-btn" onClick={() => setShowTemplates(true)}
              className="h-8 px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs font-medium rounded-lg shadow-none">
              <Copy className="w-3.5 h-3.5" /> From Template
            </Button>
            <Button data-testid="create-blank-btn" onClick={startNew}
              className="h-8 px-3 bg-brand text-[#333333] gap-1.5 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Create New
            </Button>
          </div>
        </div>

        {/* Existing Custom Scenarios */}
        {scenarios.length === 0 ? (
          <div className="heritage-card p-10 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate" />
            <p className="font-heading text-base font-bold mb-1">No custom scenarios yet</p>
            <p className="font-body text-sm text-slate mb-4">Create one from scratch or use an industry template to get started.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="custom-scenarios-list">
            {scenarios.map((s, i) => (
              <div key={s.id} className="heritage-card p-5 flex items-center gap-4" data-testid={`custom-scenario-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${s.color}15` }}>
                  <Brain className="w-6 h-6" style={{ color: s.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-heading text-sm font-bold truncate">{s.title}</p>
                    {s.industry && <Badge className="bg-muted text-slate border-none font-body text-[10px] px-1.5 py-0">{s.industry}</Badge>}
                    <Badge className="bg-brand/10 text-brand border-none font-body text-[10px] px-1.5 py-0">{s.category}</Badge>
                  </div>
                  <p className="font-body text-xs text-slate mt-0.5 truncate">{s.description}</p>
                  <div className="flex items-center gap-3 mt-1 font-body text-[10px] text-slate">
                    <span>{s.difficulties?.length || 0} difficulties</span>
                    <span>{s.variations?.length || 0} variations</span>
                    <span>{s.skills?.length || 0} skills</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button data-testid={`edit-scenario-${i}`} onClick={() => startEdit(s)}
                    className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-muted transition-colors" title="Edit">
                    <Pencil className="w-3.5 h-3.5 text-slate" />
                  </button>
                  <button data-testid={`delete-scenario-${i}`} onClick={() => setDeleteConfirm(s.id)}
                    className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-destructive/10 transition-colors" title="Delete">
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="border border-border shadow-elevated bg-surface max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-warning" /> Industry Templates
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-slate">
              Choose a template to pre-fill your scenario. You can customize everything after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {templates.map((industry, idx) => (
              <div key={idx}>
                <button data-testid={`industry-${idx}`}
                  onClick={() => setExpandedTemplate(expandedTemplate === idx ? null : idx)}
                  className="w-full flex items-center justify-between bg-muted rounded-lg px-4 py-3 hover:bg-stone/50 transition-colors">
                  <span className="font-heading text-sm font-bold">{industry.industry}</span>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-surface text-slate border border-border font-body text-[10px] px-1.5 py-0">
                      {industry.templates.length} template{industry.templates.length !== 1 ? 's' : ''}
                    </Badge>
                    {expandedTemplate === idx ? <ChevronUp className="w-4 h-4 text-slate" /> : <ChevronDown className="w-4 h-4 text-slate" />}
                  </div>
                </button>
                {expandedTemplate === idx && (
                  <div className="space-y-2 mt-2 pl-2">
                    {industry.templates.map((tmpl, tIdx) => (
                      <div key={tIdx} className="heritage-card p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${tmpl.color}15` }}>
                          <Brain className="w-5 h-5" style={{ color: tmpl.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-sm font-bold">{tmpl.title}</p>
                          <p className="font-body text-xs text-slate truncate">{tmpl.description}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {tmpl.skills?.slice(0, 3).map(s => (
                              <span key={s} className="font-body text-[10px] bg-muted text-slate rounded-full px-2 py-0">{s}</span>
                            ))}
                          </div>
                        </div>
                        <Button data-testid={`use-template-${idx}-${tIdx}`}
                          onClick={() => startFromTemplate({ ...tmpl, industry: industry.industry })}
                          className="heritage-btn bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs flex-shrink-0">
                          <Plus className="w-3.5 h-3.5" /> Use
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="border border-border shadow-elevated bg-surface max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Delete Scenario?</DialogTitle>
            <DialogDescription className="font-body text-sm text-slate">
              This action cannot be undone. The scenario will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setDeleteConfirm(null)}
              className="flex-1 heritage-btn bg-surface border border-border text-foreground hover:bg-muted text-sm h-9">
              Cancel
            </Button>
            <Button data-testid="confirm-delete-btn" onClick={() => deleteScenario(deleteConfirm)}
              className="flex-1 heritage-btn bg-destructive text-white text-sm h-9">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
